import { useState, useEffect, useRef, Suspense } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { skipToken } from "@reduxjs/toolkit/query";

import { RootState } from "@/store/store";
import { setNewTokens } from '@/store/reducers/Auth';
import { useValidateQuery } from "@/services/auth.service";
import {
  ensureFreshTokens,
  isAccessTokenFresh,
  tryRefreshTokens,
} from "@/services/tokenRefresh";

import { LoaderBackdrop } from "@/components";

const isTokenRejected = (error: unknown) => {
  const status = (error as { status?: unknown })?.status;
  return status === 401 || status === 403;
};

const AuthMiddleware = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [ validSession, setValidSession ] = useState<boolean | null>(null);
  const [ hasAttemptedRefresh, setHasAttemptedRefresh ] = useState(false);

  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.auth.isAuthenticated
  );
  const accessToken = useSelector(
    (state: RootState) => state.auth.auth.accessToken
  );
  const refreshToken = useSelector(
    (state: RootState) => state.auth.auth.refreshToken
  );

  /**
   * Los tokens vivos. `ensureFreshTokens` y `tryRefreshTokens` esperan un lock
   * antes de mirarlos, y para cuando les toca el turno los valores del render
   * que armó el callback ya pueden estar viejos: leerlos de acá evita pedir un
   * refresh con un token que otra pestaña ya rotó.
   */
  const tokensRef = useRef({ accessToken, refreshToken });
  tokensRef.current = { accessToken, refreshToken };

  const { isLoading: isValidatingToken, error: validationError } = useValidateQuery(
    accessToken ? { accessToken } : skipToken
  );

  useEffect(() => {
    if (!isAuthenticated) {
      if (location.pathname !== "/login") {
        /* La pantalla a la que iba se guarda para volver despues de loguearse:
           al abrir un pedido en pestania nueva, caer al login y terminar en el
           dashboard obligaba a buscar el pedido de nuevo a mano. */
        navigate("/login", {
          replace: true,
          state: { from: `${location.pathname}${location.search}` },
        });
      }
      setValidSession(false);
    }
  }, [isAuthenticated]);

  /**
   * Al montar solo se refresca si el access token ya venció. Antes se pedía uno
   * nuevo siempre, y como el refresh token es de un solo uso, abrir un pedido
   * en pestaña nueva rotaba el token de la sesión entera: cualquier tropiezo en
   * esa rotación —o dos pestañas abriendo a la vez— tiraba a Jenifer al login.
   * Con el token vigente no se toca nada y decide la validación de más abajo.
   */
  useEffect(() => {
    if (!isAuthenticated || hasAttemptedRefresh || validSession !== null) return;

    setHasAttemptedRefresh(true);
    if (isAccessTokenFresh(accessToken)) return;

    let active = true;

    tryRefreshTokens({
      getStoreTokens: () => tokensRef.current,
      onNewTokens: (tokens) =>
        dispatch(setNewTokens({ ...tokens, userInfo: null })),
    }).then((outcome) => {
      if (active) {
        if (outcome === 'refreshed') {
          setValidSession(true);
        }
      }
    });

    return () => {
      active = false;
    };
  }, [isAuthenticated, hasAttemptedRefresh, validSession, refreshToken, dispatch]);

  useEffect(() => {
    if (!isAuthenticated || isValidatingToken) return;

    if (!validationError || !isTokenRejected(validationError)) {
      setValidSession(true);
      return;
    }

    let active = true;
    ensureFreshTokens({
      staleAccessToken: accessToken,
      getStoreTokens: () => tokensRef.current,
      onNewTokens: (tokens) =>
        dispatch(setNewTokens({ ...tokens, userInfo: null })),
    }).then((outcome) => {
      if (active) {
        if (outcome === 'refreshed') {
          setValidSession(true);
        } else if (outcome === 'invalid') {
          setValidSession(false);
        } else {
          /*
           * 'unavailable': el servicio de auth no contesto (5xx o la request ni
           * salio). No dijo que la sesion este mal, asi que no se la tira: se
           * deja entrar con los tokens que ya hay y cada llamada al back sigue
           * validando por su cuenta. Mandarla al login por un tropiezo nuestro
           * era peor, y quedarse en null colgaba la app en el backdrop.
           */
          setValidSession(true);
        }
      }
    });
    return () => {
      active = false;
    };
  }, [isAuthenticated, isValidatingToken, validationError, accessToken, refreshToken, dispatch]);

  useEffect(() => {
    if (validSession === false && location.pathname !== "/login") {
      navigate("/login", {
        replace: true,
        state: { from: `${location.pathname}${location.search}` },
      });
    }
  }, [validSession, navigate]);

  useEffect(() => {
    if (!validSession) return;

    const path = window.location.pathname;
    if (path !== "/login" && path !== "/login/") return;

    navigate("/", { replace: true, state: null });
  }, [validSession, location, navigate]);

  return (
    <Suspense fallback={<LoaderBackdrop />}>
      {validSession === null ? <LoaderBackdrop /> : <Outlet />}
    </Suspense>
  );

};

export default AuthMiddleware;
